import axios from "axios";
import { getDeviceInfo } from "../../helpers/getDeviceInfo";
import { config } from "../../../config/env";
import traceId from "../../../redux/traceId";
import { pollUntil } from "../../services/polling/pollUntil";

export const SEND_FIRST_ACCESS_CODE_URL = `${config.jusfyBackend.apiUrl}/auth/first-access/send-code`;
export const REGISTRATION_READY_URL = `${config.jusfyBackend.apiUrl}/auth/first-access/registration-ready`;
export const VERIFY_FIRST_ACCESS_CODE_URL = `${config.jusfyBackend.apiUrl}/auth/first-access/verify-code`;
export const RESEND_FIRST_ACCESS_CODE_URL = `${config.jusfyBackend.apiUrl}/auth/first-access/resend-code`;
export const DENY_FIRST_ACCESS_URL = `${config.jusfyBackend.apiUrl}/auth/first-access/deny`;
export const SETUP_FIRST_ACCESS_PASSWORD_URL = `${config.jusfyBackend.apiUrl}/auth/first-access/setup-password`;

export const REGISTRATION_READY_AUTO_WAIT_TIMEOUT_MS = 120_000;
export const REGISTRATION_READY_POLL_INTERVAL_MS = 12_000;

export interface FirstAccessRecaptchaPayload {
  recaptchaToken: string;
  recaptchaVersion: string;
}

export function sendFirstAccessCode(
  email: string,
  recaptcha: FirstAccessRecaptchaPayload,
) {
  return axios.post(
    SEND_FIRST_ACCESS_CODE_URL,
    {
      email,
      recaptchaToken: recaptcha.recaptchaToken,
      recaptchaVersion: recaptcha.recaptchaVersion,
    },
    {
      headers: {
        "x-trace-id": traceId,
      },
    },
  );
}

export async function waitFirstAccessRegistrationReady(
  email: string,
  options: {
    signal?: AbortSignal;
    timeoutMs?: number;
    pollIntervalMs?: number;
    recaptcha?: FirstAccessRecaptchaPayload;
    getRecaptcha?: () => FirstAccessRecaptchaPayload | undefined;
    refreshRecaptcha?: () => void;
  } = {},
): Promise<boolean> {
  const normalizedEmail = email.trim().toLowerCase();

  return pollUntil({
    request: async (signal) => {
      const recaptcha = options.getRecaptcha?.() ?? options.recaptcha;
      const { data: responseData } = await axios.post(
        REGISTRATION_READY_URL,
        {
          email: normalizedEmail,
          recaptchaToken: recaptcha?.recaptchaToken,
          recaptchaVersion: recaptcha?.recaptchaVersion,
        },
        {
          headers: {
            "x-trace-id": traceId,
          },
          signal,
        },
      );

      return Boolean(responseData?.data?.ready);
    },
    isReady: (ready) => ready,
    timeoutMs: options.timeoutMs ?? REGISTRATION_READY_AUTO_WAIT_TIMEOUT_MS,
    intervalMs: options.pollIntervalMs ?? REGISTRATION_READY_POLL_INTERVAL_MS,
    signal: options.signal,
    onPollAttempt: options.refreshRecaptcha,
  });
}

export function verifyFirstAccessCode(code: string, token: string) {
  const deviceInfo = getDeviceInfo();

  return axios.post(
    VERIFY_FIRST_ACCESS_CODE_URL,
    { code },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "x-trace-id": traceId,
        "x-device-id": deviceInfo.deviceFingerprint,
      },
    },
  );
}

export function resendFirstAccessCode(token: string) {
  return axios.post(
    RESEND_FIRST_ACCESS_CODE_URL,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "x-trace-id": traceId,
      },
    },
  );
}

export function denyFirstAccess(email: string) {
  return axios.post(
    DENY_FIRST_ACCESS_URL,
    { email },
    {
      headers: {
        "x-trace-id": traceId,
      },
    },
  );
}

export function setupPassword(
  password: string,
  confirmationPassword: string,
  token: string
) {
  return axios.post(
    SETUP_FIRST_ACCESS_PASSWORD_URL,
    {
      password,
      confirmation_password: confirmationPassword,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "x-trace-id": traceId,
      },
    }
  );
}
